import type {
  ThemeId,
  AspirationId,
  TimeBudgetId,
  ObstacleId,
  ReflectionControlAnswer,
  ReflectionAreaId,
  ReflectionAreaAnswer,
} from "./types";
import {
  THEME_ORDER,
  ASPIRATION_ORDER,
  TIME_BUDGET_ORDER,
  OBSTACLE_ORDER,
  REFLECTION_AREA_ORDER,
} from "./types";

export const MAX_THEMES = 3;
export const MAX_ASPIRATIONS = 3;
export const MAX_OBSTACLES = 3;
export const MAX_WEEKLY_REFLECTIONS = 8;

export const FREE_TEXT_MAX = 600;
export const USER_INTERPRETATION_MAX = 400;
export const WEEKLY_REFLECTION_MAX = 600;

export interface ThemeOption {
  id: ThemeId;
  label: string;
  hint: string;
}

export const THEMES: readonly ThemeOption[] = [
  {
    id: "freedom",
    label: "Freedom & Autonomy",
    hint: "Setting your own pace, choosing your direction, and shedding arbitrary pressures.",
  },
  {
    id: "love",
    label: "Deep Love & Kinship",
    hint: "Being known and cherished without masks, sharing warmth, and trusting deeply.",
  },
  {
    id: "confidence",
    label: "Grounded Confidence",
    hint: "Trusting your judgment, occupying space with ease, and moving without apology.",
  },
  {
    id: "creativity",
    label: "Creative Vitality",
    hint: "Bringing original ideas to life, playing with form, and feeling generative.",
  },
  {
    id: "adventure",
    label: "Frontier & Adventure",
    hint: "Stepping outside the familiar, meeting fresh horizons, and feeling awake.",
  },
  {
    id: "achievement",
    label: "Craft & Achievement",
    hint: "Building demonstrable skill, finishing what matters, and feeling proud of your work.",
  },
  {
    id: "belonging",
    label: "Belonging & Community",
    hint: "Having a recognized seat at the table and contributing to something wider than yourself.",
  },
  {
    id: "purpose",
    label: "Clear Purpose",
    hint: "Knowing why you get up, aligning your hours with your values, and feeling useful.",
  },
] as const;

export interface AspirationOption {
  id: AspirationId;
  label: string;
  area: string;
  hint: string;
}

export const ASPIRATIONS: readonly AspirationOption[] = [
  {
    id: "health",
    label: "Health & Energy",
    area: "Energy, sleep, body",
    hint: "Restoring physical stamina, evening sleep rhythms, and feeling at home in your body.",
  },
  {
    id: "craft",
    label: "Creative Craft",
    area: "Making or creating something",
    hint: "Building an art form, writing, music, code, or tangible making into regular life.",
  },
  {
    id: "career",
    label: "Work Direction",
    area: "Work direction & vocation",
    hint: "Clarifying your trade, shifting your responsibilities, or advancing toward meaningful work.",
  },
  {
    id: "relationships",
    label: "Closer Relationships",
    area: "Closer personal ties",
    hint: "Nurturing genuine friendships, honest romance, and reliable emotional closeness.",
  },
  {
    id: "learning",
    label: "Deep Learning",
    area: "A skill or subject",
    hint: "Mastering a difficult topic, reading deeply, or acquiring an instrument or language.",
  },
  {
    id: "adventure",
    label: "Novel Adventure",
    area: "New places & experiences",
    hint: "Breaking monotonous routines, visiting unfamiliar ground, and testing your limits.",
  },
  {
    id: "money",
    label: "Financial Stability",
    area: "Stability & savings",
    hint: "Building a buffer, understanding your expenses, and reducing baseline financial worry.",
  },
  {
    id: "community",
    label: "Local Community",
    area: "Giving back & belonging",
    hint: "Showing up for your neighborhood, joining a shared project, and helping others directly.",
  },
  {
    id: "calm",
    label: "Calm & Space",
    area: "Rest, space, routine",
    hint: "Reclaiming quiet hours, reducing sensory overwhelm, and building a predictable harbor.",
  },
  {
    id: "voice",
    label: "Finding Your Voice",
    area: "Speaking up & being seen",
    hint: "Expressing your true view, setting clear boundaries, and sharing your perspective openly.",
  },
] as const;

export interface TimeBudgetOption {
  id: TimeBudgetId;
  label: string;
  description: string;
  cadence: "daily" | "weekly";
}

export const TIME_BUDGETS: readonly TimeBudgetOption[] = [
  {
    id: "10min-day",
    label: "10 minutes a day",
    description: "A compact daily sliver designed to build rhythm without competing with obligations.",
    cadence: "daily",
  },
  {
    id: "30min-day",
    label: "30 minutes a day",
    description: "A focused daily block for steady practice, study, or visible project momentum.",
    cadence: "daily",
  },
  {
    id: "1hr-week",
    label: "1 hour a week",
    description: "A single weekly sanctuary session for deep focus or an exploratory outing.",
    cadence: "weekly",
  },
  {
    id: "3hr-week",
    label: "3 hours a week",
    description: "Substantial weekly time that can split into two or three focused working sessions.",
    cadence: "weekly",
  },
] as const;

export interface ObstacleOption {
  id: ObstacleId;
  label: string;
  hint: string;
  generalResponses: [string, string];
  aspirationResponses?: Partial<Record<AspirationId, string>>;
}

export const OBSTACLES: readonly ObstacleOption[] = [
  {
    id: "no-time",
    label: "No spare time",
    hint: "Calendar packed with demands, leaving little margin for personal initiatives.",
    generalResponses: [
      "Shrink the initial action until it takes under five minutes. Momentum beats duration.",
      "Attach the action to an existing anchor you never skip, like morning coffee or your commute.",
    ],
    aspirationResponses: {
      health: "Prioritize sleep and three five-minute movement breaks instead of adding workout obligations.",
      craft: "Keep materials open on your desk so you can write or sketch for two minutes between tasks.",
      learning: "Swap 10 minutes of passive scrolling for one article or audio chapter during transit.",
      calm: "Do not schedule calm. Claim two minutes of eyes-closed stillness before entering your home.",
    },
  },
  {
    id: "low-energy",
    label: "Low physical or mental energy",
    hint: "Feeling drained at the end of responsibilities, with little fuel left for self-directed effort.",
    generalResponses: [
      "Lower the barrier to entry: prepare your tools when rested so action requires zero setup.",
      "Honor tiredness by choosing restorative actions rather than demanding high output.",
    ],
    aspirationResponses: {
      health: "Begin with hydration, light stretching, and an earlier bedtime before trying demanding fitness.",
      craft: "Consume inspiring work passively when tired, or organize references without pressure to create.",
      calm: "Lie down in a quiet room for ten minutes with phone in airplane mode before demanding productivity.",
    },
  },
  {
    id: "fear-of-failing",
    label: "Fear of failing or looking foolish",
    hint: "Hesitation to begin because the outcome might fall short of expectations or reveal inexperience.",
    generalResponses: [
      "Define success for the next 14 days solely by showing up, completely separating practice from quality.",
      "Keep early experiments private. You do not owe anyone an announcement while exploring.",
    ],
    aspirationResponses: {
      voice: "Practice your perspective in low-stakes environments or an anonymous notebook first.",
      career: "Treat exploratory outreach as informational interviews with zero expectation of an offer.",
      craft: "Design your first five attempts to be throwaway drafts intended for no one else to see.",
    },
  },
  {
    id: "perfectionism",
    label: "Perfectionism & all-or-nothing thinking",
    hint: "Believing that if it cannot be done thoroughly or flawlessly, it is not worth doing.",
    generalResponses: [
      "Aim for a messy, incomplete version first. An imperfect page exists; a flawless idea does not.",
      "Replace 'doing it right' with 'doing it slightly.' A 5-minute walk beats an aborted gym routine.",
    ],
    aspirationResponses: {
      craft: "Set a timer for 15 minutes and intentionally produce a rough, unpolished sketch.",
      learning: "Learn the 20% most common terminology before touching comprehensive textbooks.",
      money: "Track just three major spending categories for one week rather than building a master spreadsheet.",
    },
  },
  {
    id: "self-doubt",
    label: "Self-doubt & imposter feelings",
    hint: "A recurring sense that other people are qualified for this, but you are somehow an outsider.",
    generalResponses: [
      "Recognize that competence follows action, never precedes it. Everyone feels clumsy when learning.",
      "Focus on the task directly in front of you instead of asking what kind of person you are.",
    ],
    aspirationResponses: {
      career: "Write down three problems you solved this past year. Concrete facts counter generalized doubts.",
      community: "Attend the first meeting simply to listen and set up chairs. Contribution starts small.",
      voice: "Share an honest observation rather than an expert verdict. Observations cannot be invalidated.",
    },
  },
  {
    id: "distraction",
    label: "Distraction & digital overload",
    hint: "Attention scattered across tabs, notifications, and endless informational loops.",
    generalResponses: [
      "Put your phone in another room or inside a drawer before beginning your focus session.",
      "Work with pen and paper for the first ten minutes to protect your working memory.",
    ],
    aspirationResponses: {
      craft: "Disconnect your workstation from the internet while working on the first draft.",
      learning: "Read a physical book or printed article instead of an open browser window with tabs.",
      relationships: "Place phones facedown on silent during meals and conversations.",
    },
  },
  {
    id: "others-opinions",
    label: "Worry about others' opinions",
    hint: "Anticipating skepticism, unsolicited commentary, or judgment from family or peers.",
    generalResponses: [
      "Hold your initial moves in quiet privacy until your new routine has built its own root system.",
      "Remember that people project their own anxieties and comfort zones when evaluating others.",
    ],
    aspirationResponses: {
      voice: "Remember that silence is also a choice that carries consequences for your own well-being.",
      adventure: "Plan a small solo afternoon excursion where you do not need consensus or approval.",
      money: "Align spending with your personal values quietly, without needing to justify lifestyle shifts.",
    },
  },
  {
    id: "losing-momentum",
    label: "Losing momentum after the initial spark",
    hint: "Starting with enthusiasm but dropping off as novelty fades into routine.",
    generalResponses: [
      "Expect the dip after day four. Plan ahead for how you will handle a missed day with self-kindness.",
      "Never miss twice in a row: if you skip yesterday, do a 60-second micro-version today.",
    ],
    aspirationResponses: {
      health: "Keep gym shoes by the door and water on the nightstand so resuming takes zero friction.",
      craft: "End each work session by writing down the exact next sentence or stroke for tomorrow.",
      learning: "Schedule a fixed weekly calendar slot so progress does not depend on spontaneous mood.",
    },
  },
] as const;

export const REFLECTION_CONTROL: readonly ReflectionControlAnswer[] = [
  "mostly in my control",
  "sometimes hard to steer",
  "often hard to stop",
  "prefer not to say",
] as const;

export interface ReflectionAreaOption {
  id: ReflectionAreaId;
  label: string;
  hint: string;
}

export const REFLECTION_AREAS: readonly ReflectionAreaOption[] = [
  {
    id: "sleep",
    label: "Sleep & rest",
    hint: "Does staying in imagined worlds delay your sleep or keep you awake late into the night?",
  },
  {
    id: "work",
    label: "Work obligations",
    hint: "Does drift pull attention away from deadlines, meetings, or daytime focus?",
  },
  {
    id: "study",
    label: "Study & coursework",
    hint: "Does immersive reverie compete with reading, assignments, or classes?",
  },
  {
    id: "relationships",
    label: "In-person relationships",
    hint: "Do imagined scenarios substitute for real conversations or cause you to withdraw?",
  },
  {
    id: "goals",
    label: "Everyday practical goals",
    hint: "Does dreaming replace taking physical steps toward the changes you care about?",
  },
] as const;

export const REFLECTION_ANSWERS: readonly ReflectionAreaAnswer[] = [
  "no",
  "a little",
  "quite a bit",
  "prefer not to say",
] as const;

export const INTERPRETATION_DISCLAIMER =
  "Educational suggestion. The meaning of an imagined identity is deeply personal and no algorithm can determine it. You are always the authority on your own inner world: feel free to accept, reword, or set this interpretation aside.";
